import { useSyncExternalStore } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useUserRole } from '@/hooks/useUserRole';

export type SiteDesignMode = 'classic' | 'preview' | 'new';

// Which storefront design is live, set in Admin > Website > Settings > Design
// and stored as the 'site.design' row in rxk_site_content (locale 'en').
//   classic: everyone sees the original design
//   preview: staff (anyone with a user_roles row) see the new design,
//            customers still see the classic one
//   new:     everyone sees the new design
//
// The mode is fetched on its own (one row, not the whole content table) and
// remembered in the browser, so a returning visitor gets the right design on
// the very first paint instead of a flash of the classic one. Until the mode
// is known for the first time, `ready` is false and App shows a blank page.
// A failed fetch falls back to 'classic', so the switch can never take the
// storefront down.

const MODE_KEY = 'rxk-site-design';
const STAFF_KEY = 'rxk-site-design-staff';
const listeners = new Set<() => void>();

const read = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* storage blocked: the fetched value still applies for this visit */
  }
};
const asMode = (v: string | null | undefined): SiteDesignMode | null => (v === 'classic' || v === 'preview' || v === 'new' ? v : null);

let mode: SiteDesignMode | null = asMode(read(MODE_KEY));
let fetched = false;

function setMode(next: SiteDesignMode) {
  if (next !== mode) {
    mode = next;
    listeners.forEach((l) => l());
  }
  write(MODE_KEY, next);
}

// Lets the admin switch apply in this browser straight away.
export function rememberSiteDesign(next: SiteDesignMode) {
  setMode(next);
}

function subscribe(l: () => void) {
  listeners.add(l);
  if (!fetched) {
    fetched = true;
    supabase
      .from('rxk_site_content')
      .select('value')
      .eq('key', 'site.design')
      .eq('locale', 'en')
      .maybeSingle()
      .then(
        ({ data, error }) => setMode(error ? (mode ?? 'classic') : (asMode((data as { value: string } | null)?.value) ?? 'classic')),
        () => setMode(mode ?? 'classic'),
      );
  }
  return () => listeners.delete(l);
}

export function useSiteDesign() {
  const current = useSyncExternalStore(subscribe, () => mode);
  const { role, loading } = useUserRole();
  const cachedStaff = read(STAFF_KEY);
  if (!loading) write(STAFF_KEY, role ? '1' : '0');
  const staff = loading ? cachedStaff === '1' : !!role;
  const m: SiteDesignMode = current ?? 'classic';
  const isNew = m === 'new' || (m === 'preview' && staff);
  // Preview depends on who is signed in; wait for that unless this browser has seen it before.
  const ready = current !== null && (m !== 'preview' || !loading || cachedStaff !== null);
  return { mode: m, isNew, isPreview: m === 'preview' && staff, ready };
}
